/**
 * staff-sleutel — één antwoord op "wie is deze medewerker", gedeeld door de
 * api-laag (api/_lib/staff-sleutel.ts leest hier vandaan) en de frontend.
 *
 * Tot de cut kreeg elke staff-rij een `Medewerker__c` in Salesforce en droeg
 * `staff.sf_medewerker_id` die sleutel. Sinds `SF_SYNC_KOPIE_UIT` aanstaat
 * ontstaat er geen Medewerker__c meer, dus een nieuwe collega houdt dat veld
 * permanent leeg. Elke plek die daarop filterde liet hem stil vallen: niet
 * kiesbaar als chauffeur of bijrijder, niet toewijsbaar in het rooster, en
 * geweigerd door de field-app (Hans de Vries, gevonden 08-09).
 *
 * De sleutel is daarom: het SF-id waar dat bestaat, anders de eigen `staff.id`.
 * Bestaande koppelingen houden hun SF-id, nieuwe krijgen een uuid, en elke
 * lezer matcht op allebei — geen backfill, geen big-bang.
 */

export interface StaffSleutelRij {
  id?: unknown;
  sf_medewerker_id?: unknown;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True als de sleutel een eigen `staff.id` is (en dus geen Salesforce-id). */
export function isEigenStaffSleutel(sleutel: string): boolean {
  return UUID_RE.test(sleutel);
}

/**
 * De sleutel van een staff-rij: SF-id waar het bestaat, anders de eigen id.
 * Dit is wat naar buiten gaat (dropdowns, DTO's) én wat in een koppelkolom
 * wordt weggeschreven.
 */
export function staffSleutel(rij: StaffSleutelRij): string {
  const sf = rij.sf_medewerker_id;
  if (typeof sf === 'string' && sf !== '') return sf;
  return String(rij.id ?? '');
}

/** Matcht een staff-rij tegen een opgeslagen sleutel (SF-id of eigen id). */
export function staffRijMatchtSleutel(rij: StaffSleutelRij, sleutel: string): boolean {
  if (!sleutel) return false;
  return rij.sf_medewerker_id === sleutel || String(rij.id ?? '') === sleutel;
}
